import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../lib/api-client';
const AuthContext = createContext(null);
const ACCESS_TOKEN_KEY = 'watad_access_token';
const REFRESH_TOKEN_KEY = 'watad_refresh_token';
const ACTIVE_BRANCH_KEY = 'watad_active_branch_id';
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [branches, setBranches] = useState([]);
    const [activeBranch, setActiveBranchState] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Load branches
    const loadBranches = useCallback(async () => {
        try {
            const res = await apiClient.get('/branches');
            const branchList = res.data;
            setBranches(branchList);
            const savedBranchId = localStorage.getItem(ACTIVE_BRANCH_KEY);
            const matched = branchList.find((b) => b.id === savedBranchId) || branchList.find((b) => b.isDefault) || branchList[0];
            if (matched) {
                setActiveBranchState(matched);
            }
        }
        catch {
            // Ignore if not logged in yet
        }
    }, []);
    const demoUser = {
        id: '00000000-0000-0000-0000-000000000001',
        email: 'admin@watad.local',
        name: 'المدير العام (Demo Admin)',
        tenantId: '00000000-0000-0000-0000-000000000001',
        roles: ['super_admin', 'company_owner'],
        permissions: [
            'CUSTOMERS_VIEW', 'CUSTOMERS_CREATE', 'CUSTOMERS_EDIT', 'CUSTOMERS_DELETE',
            'SUPPLIERS_VIEW', 'SUPPLIERS_CREATE', 'SUPPLIERS_EDIT', 'SUPPLIERS_DELETE',
            'PRODUCTS_VIEW', 'PRODUCTS_CREATE', 'PRODUCTS_EDIT', 'PRODUCTS_DELETE',
            'INVENTORY_VIEW', 'INVENTORY_ADJUST', 'INVENTORY_TRANSFER',
            'SALES_INVOICE_VIEW', 'SALES_INVOICE_CREATE', 'SALES_INVOICE_POST', 'SALES_INVOICE_CANCEL',
            'PURCHASE_INVOICE_VIEW', 'PURCHASE_INVOICE_CREATE', 'PURCHASE_INVOICE_POST', 'PURCHASE_INVOICE_CANCEL',
            'TREASURY_VIEW', 'TREASURY_RECEIVE', 'TREASURY_PAY', 'TREASURY_TRANSFER',
            'REPORTS_VIEW', 'SETTINGS_MANAGE', 'USERS_MANAGE', 'BRANCHES_MANAGE', 'COMPANIES_MANAGE', 'SYSTEM_ADMIN'
        ],
        branchAccess: ['00000000-0000-0000-0000-000000000010'],
    };
    const demoBranches = [
        {
            id: '00000000-0000-0000-0000-000000000010',
            tenantId: '00000000-0000-0000-0000-000000000001',
            createdAt: '2026-01-01T00:00:00Z',
            updatedAt: '2026-01-01T00:00:00Z',
            companyId: '00000000-0000-0000-0000-000000000001',
            code: 'HQ-CAIRO',
            name: 'الفرع الرئيسي — القاهرة',
            nameAr: 'الفرع الرئيسي — القاهرة',
            isDefault: true,
            status: 'ACTIVE',
            address: 'القاهرة، مصر',
        },
    ];
    // Check current session on mount
    useEffect(() => {
        async function checkAuth() {
            const token = localStorage.getItem(ACCESS_TOKEN_KEY);
            if (!token) {
                setIsLoading(false);
                return;
            }
            if (token === 'demo-access-token') {
                setUser(demoUser);
                setBranches(demoBranches);
                setActiveBranchState(demoBranches[0] || null);
                setIsLoading(false);
                return;
            }
            try {
                const res = await apiClient.get('/auth/me');
                setUser(res.data.user);
                await loadBranches();
            }
            catch {
                // If token is invalid or backend unreachable in static demo, check if demo token
                localStorage.removeItem(ACCESS_TOKEN_KEY);
                setUser(null);
            }
            finally {
                setIsLoading(false);
            }
        }
        void checkAuth();
    }, [loadBranches]);
    const login = async (email, password) => {
        // If it's the demo account or static preview, log in immediately
        if (email === 'admin@watad.local' || email === 'demo@watad.local') {
            localStorage.setItem(ACCESS_TOKEN_KEY, 'demo-access-token');
            localStorage.setItem(REFRESH_TOKEN_KEY, 'demo-refresh-token');
            if (demoBranches[0]) {
                localStorage.setItem(ACTIVE_BRANCH_KEY, demoBranches[0].id);
            }
            setUser(demoUser);
            setBranches(demoBranches);
            setActiveBranchState(demoBranches[0] || null);
            return;
        }
        try {
            const res = await apiClient.post('/auth/login', { email, password });
            const { session } = res.data;
            localStorage.setItem(ACCESS_TOKEN_KEY, session.tokens.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, session.tokens.refreshToken);
            setUser(session.user);
            await loadBranches();
        }
        catch (err) {
            throw err;
        }
    };
    const logout = async () => {
        try {
            await apiClient.post('/auth/logout');
        }
        catch {
            // Ignore logout API failure
        }
        finally {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(ACTIVE_BRANCH_KEY);
            setUser(null);
            setBranches([]);
            setActiveBranchState(null);
            window.location.href = '/auth/login';
        }
    };
    const setActiveBranch = (branch) => {
        setActiveBranchState(branch);
        localStorage.setItem(ACTIVE_BRANCH_KEY, branch.id);
    };
    const hasPermission = (permission) => {
        if (!user)
            return false;
        if (user.roles.includes('super_admin') || user.roles.includes('company_owner')) {
            return true;
        }
        return user.permissions.includes(permission);
    };
    const hasAnyRole = (...roles) => {
        if (!user)
            return false;
        return roles.some((role) => user.roles.includes(role));
    };
    return (_jsx(AuthContext.Provider, { value: {
            user,
            isLoading,
            isAuthenticated: !!user,
            branches,
            activeBranch,
            setActiveBranch,
            login,
            logout,
            hasPermission,
            hasAnyRole,
        }, children: children }));
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
//# sourceMappingURL=AuthContext.js.map